<template>
  <span>
    <span v-if="legend != ''" class="legend">
      {{ legend }}
    </span>
    <input
      type="text"
      class="input-number generic-border--thin-border"
      :class="classes"
      :value="textValue"
      :disabled="disabled"
      :size="size"
      @input="onInput"
      @keypress="onKeypress"
      @keyup="onKeyup"
      @blur="onBlur"
    >
  </span>
</template>

<script>
import { stringToCharCodes } from '@/utils/main';
// Constant: list of charcodes allowed in the input of a number
const CHARCODES_INPUT_NUMBER = stringToCharCodes('-+.,0123456789eE');

/**
 * Input field for number, will convert value to a Number type
 *
 * @vue-prop {Number} value - variable that the field will modify
 * @vue-prop {String} [legend=''] - Span with class 'legend' before input field
 * @vue-prop {Number} max - Maximum value
 * @vue-prop {Number} min - Minimum value
 *
 * @vue-event {Number} input - Emit updated value variable, triggered when a key is pressed
 * @vue-event {Number} confirm - Emit current value, triggered when Enter is pressed or field is blurred
 */

export default {
  name: 'InputNumber',
  props: {
    value: {
      type: Number,
      default: 0,
    },
    legend: {
      type: String,
      default: '',
    },
    max: {
      type: Number,
      default: NaN,
    },
    min: {
      type: Number,
      default: NaN,
    },
    size: {
      type: Number,
      default: NaN,
    },
    disabled: Boolean,
    refreshAfterInput: Boolean,
    setDashedBorder: Boolean,
  },
  data() {
    return {
      textValue: isNaN(this.value) ? '0' : String(this.value),
    };
  },
  computed: {
    isInvalid() {
      return isNaN(this.value);
    },
    classes() {
      return {
        'error--glow': this.isInvalid,
        'bordered-field': this.setDashedBorder,
      };
    },
  },
  watch: {
    value(newValue) {
      if (typeof newValue !== 'number') {
        this.$emit('input', this.castToNumber(newValue));
      } else {
        this.textValue = `${newValue}`;
      }
    },
  },
  methods: {
    /**
     * Convert a any value into a number ; replace comma by float before conversion if value is a string
     * Also makes sure that the value isn't out of its min, max bounds
     * @param {} value - Value to be converted to number
     * @returns {number} Value converted into number (Warning: can be NaN)
     */
    castToNumber(value) {
      if (typeof value === 'string' || value instanceof String) {
        value = value.replace(',', '.');
      }
      let val = ['-', '0-'].includes(value) ? -0 : Number(value);
      if (!isNaN(this.max) && val > this.max) {
        val = this.max;
      }
      if (!isNaN(this.min) && val < this.min) {
        val = this.min;
      }
      return isNaN(val) ? 0 : val;
    },
    /**
     * Emit 'confirm' event - Will make additional check (depending on some properties) in the future
     */
    confirm() {
      this.$emit('confirm', this.value);
    },
    /**
     * Event handler - Prevent unallowed keypress input. Warning: doesn't prevent invalid value input (copy/paste)
     * @param {KeyboardEvent} e - Keypress event object
     */
    onKeypress(e) {
      if (e.charCode > 0 && CHARCODES_INPUT_NUMBER.indexOf(e.charCode) === -1) {
        e.preventDefault();
      }
    },
    /**
     * Event handler - Emit 'confirm' event on Enter key release
     * @param {KeyboardEvent} e - Keypress event object
     */
    onKeyup(e) {
      // 13: Enter key
      if (e.keyCode === 13) {
        this.confirm(this.value);
      }
    },
    /**
     * Event handler - Emit 'confirm' on blur
     */
    onBlur() {
      this.confirm(this.value);
      this.$emit('blur');
    },
    /**
     * Event handler - On text input, will convert text to number in the 'value' property, relay 'input' event
     * @param {InputEvent} e - Input event object
     */
    onInput(e) {
      this.textValue = e.target.value;
      this.$emit('input', this.castToNumber(this.textValue));

      if (this.refreshAfterInput) {
        this.$nextTick(() => {
          this.textValue = `${this.value}`;
        });
      }
    },
  },
};
</script>
